import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Github, ArrowLeft, Folder } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTranslatedPageData } from "../utils/projectTranslations";
import { useNavigation } from "../contexts/NavigationContext";
import Navbar from "../components/Navbar";

const Archive = () => {
  const { t } = useTranslation();
  const [hoveredRow, setHoveredRow] = useState(null);
  const translatedPageData = useTranslatedPageData();
  const location = useLocation();
  const navigate = useNavigate();
  const { goBackToPrevious } = useNavigation();

  const handleBackClick = (e) => {
    e.preventDefault();
    goBackToPrevious(navigate);
  };

  const getProjectYear = (project) => {
    const repoYears = {
      [t("projects.audiophile.title")]: "2024",
      [t("projects.freshmart.title")]: "2022",
    };

    return repoYears[project.title] || "2023";
  };

  // Use translatedPageData as the primary source (it has more complete project info)
  const allProjects = translatedPageData.map((project) => ({
    year: getProjectYear(project),
    title: project.title,
    madeAt: t("archive.personal"),
    builtWith: project.techStack || [],
    links: {
      github: project.github,
      external: project.live,
    },
  }));

  return (
    <div className="min-h-screen bg-navy text-white">
      {/* Navbar */}
      <Navbar />

      {/* Back Navigation */}
      <div className="pt-24 pb-8">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <button
            onClick={handleBackClick}
            className="flex items-center space-x-3 text-accent hover:text-accent/80 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-mono text-sm">{t("archive.back")}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="pb-20">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Page Title */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-lighterSlate mb-4">
              {t("archive.title")}
            </h1>
            <p className="text-accent font-mono text-base">
              {t("archive.subtitle")}
            </p>
          </motion.div>

          {/* Projects Table */}
          <motion.div
            className="overflow-x-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-accent/20">
                  <th className="text-left py-4 px-2 text-sm font-mono text-accent font-normal">
                    {t("archive.tableHeaders.year")}
                  </th>
                  <th className="text-left py-4 px-2 text-sm font-mono text-accent font-normal">
                    {t("archive.tableHeaders.project")}
                  </th>
                  <th className="text-left py-4 px-2 text-sm font-mono text-accent font-normal hidden sm:table-cell">
                    {t("archive.tableHeaders.madeAt")}
                  </th>
                  <th className="text-left py-4 px-2 text-sm font-mono text-accent font-normal hidden md:table-cell">
                    {t("archive.tableHeaders.builtWith")}
                  </th>
                  <th className="text-left py-4 px-2 text-sm font-mono text-accent font-normal">
                    {t("archive.tableHeaders.link")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {allProjects.map((project, index) => (
                  <motion.tr
                    key={`${project.title}-${index}`}
                    className={`border-b border-accent/10 hover:bg-lightNavy/20 transition-all duration-200 ${
                      hoveredRow === index ? "bg-lightNavy/20" : ""
                    }`}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    {/* Year */}
                    <td className="py-6 px-2">
                      <span className="text-accent font-mono text-sm">
                        {project.year}
                      </span>
                    </td>

                    {/* Title */}
                    <td className="py-6 px-2">
                      <div className="flex items-center space-x-3">
                        <Folder className="w-4 h-4 text-accent flex-shrink-0" />
                        <span className="text-lighterSlate font-semibold hover:text-accent transition-colors duration-200">
                          {project.title}
                        </span>
                      </div>
                    </td>

                    {/* Made at */}
                    <td className="py-6 px-2 hidden sm:table-cell">
                      <span className="text-slate text-sm">
                        {project.madeAt}
                      </span>
                    </td>

                    {/* Built with */}
                    <td className="py-6 px-2 hidden md:table-cell">
                      <div className="flex flex-wrap gap-2">
                        {project.builtWith.slice(0, 3).map((tech) => (
                          <span
                            key={tech}
                            className="text-slate font-mono text-xs hover:text-accent transition-colors duration-200"
                          >
                            {tech}
                          </span>
                        ))}
                        {project.builtWith.length > 3 && (
                          <span className="text-slate font-mono text-xs">
                            +{project.builtWith.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Links */}
                    <td className="py-6 px-2">
                      <div className="flex items-center space-x-3">
                        {project.links.github && (
                          <a
                            href={project.links.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate hover:text-accent transition-colors duration-200 p-1"
                            aria-label="GitHub Repository"
                          >
                            <Github className="w-4 h-4" />
                          </a>
                        )}
                        {project.links.external && (
                          <a
                            href={project.links.external}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate hover:text-accent transition-colors duration-200 p-1"
                            aria-label="Live Demo"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="mt-16 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <p className="text-slate font-mono text-sm">
              {allProjects.length} {t("archive.projectsAndCounting")}
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Archive;
